import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import MssTooltip from '../../common/MssTooltip';
import { FormControlledTextField } from '../../common/Form/FormTextField';
import { QUIZ_OPTION_KEYS } from '../../common/constants';
import { getOriginLabel } from '../helpers';
import { AnswerRow } from './AnswerRow';
import { TrueFalseAnswers } from './TrueFalseAnswers';

/** Rules for a field that has to be filled in, or none at all when it doesn't. */
const requiredWhen = (isRequired, message) =>
  isRequired ? { validate: (value) => (value?.trim() ? true : message) } : undefined;

/**
 * One editable question, laid out like the quiz itself: the question, then its
 * hint and explanation, then its answers. Everything is editable in place except
 * a True/False question's two answers, which are fixed.
 *
 * Props:
 *   id       (string)   - ID prefix for generated element IDs.
 *   control  (object)   - react-hook-form control for the editor's form.
 *   index    (number)   - Zero-based position in the form's question array.
 *   number   (number)   - The question's position in the quiz, starting at 1.
 *   question (object)   - An editable question (see helpers/questionEditing).
 *   canDelete (bool)    - False when this is the only question left.
 *   onAdd    (function) - Called with the click event to add a question below this one.
 *   onDelete (function) - Remove this question.
 */
export const QuestionEditor = ({
  id,
  control,
  index,
  number,
  question,
  canDelete,
  onAdd,
  onDelete,
}) => {
  const cardId = `${id}__question_${number}`;
  const name = `questions.${index}`;
  const originLabel = getOriginLabel(question);

  /*
   * Hints and explanations arrive with a generated question, so leaving one
   * blank means something was lost. A question the learner wrote themselves
   * never had either, and is not made to invent them.
   */
  const isGenerated = !question.isManuallyAdded;

  return (
    <Box className="mss__box questionEditor" id={cardId}>
      <Box className="questionEditorHeader">
        <Box className="questionEditorHeading">
          <Typography className="questionNumber">Question {number}</Typography>
          {originLabel && (
            <Typography className="questionOrigin" id={`${cardId}__text_origin`}>
              {originLabel}
            </Typography>
          )}
        </Box>

        <Box className="questionEditorActions">
          <MssTooltip id={`${cardId}__tooltip_add`} title="Add a question below this one">
            <IconButton
              id={`${cardId}__iconButton_add`}
              className="cardActionIcon"
              size="small"
              onClick={onAdd}
              aria-label={`Add a question below question ${number}`}
            >
              <FontAwesomeIcon icon={faPlus} size="xs" />
            </IconButton>
          </MssTooltip>

          {/* Wrapped so the tooltip still fires once the last question disables it. */}
          <MssTooltip
            id={`${cardId}__tooltip_delete`}
            title={canDelete ? 'Delete question' : 'A quiz needs at least one question'}
          >
            <span className="cardActionIconWrap">
              <IconButton
                id={`${cardId}__iconButton_delete`}
                className="cardActionIcon"
                size="small"
                onClick={onDelete}
                disabled={!canDelete}
                aria-label={`Delete question ${number}`}
              >
                <FontAwesomeIcon icon={faTrash} size="xs" />
              </IconButton>
            </span>
          </MssTooltip>
        </Box>
      </Box>

      <FormControlledTextField
        id={`${cardId}__input_question`}
        name={`${name}.question`}
        control={control}
        label="Question"
        placeholder="Enter question"
        rules={requiredWhen(true, 'Enter the question.')}
        fullWidth
        multiline
      />

      <FormControlledTextField
        id={`${cardId}__input_hint`}
        name={`${name}.hint`}
        control={control}
        label="Hint"
        placeholder="Enter hint"
        rules={requiredWhen(isGenerated, 'Enter a hint.')}
        fullWidth
        multiline
      />

      <FormControlledTextField
        id={`${cardId}__input_explanation`}
        name={`${name}.explanation`}
        control={control}
        label="Answer Explanation"
        placeholder="Enter explanation"
        rules={requiredWhen(isGenerated, 'Enter an answer explanation.')}
        fullWidth
        multiline
      />

      <Box className="answerSection">
        {question.isTrueFalse ? (
          <TrueFalseAnswers id={cardId} control={control} name={name} questionNumber={number} />
        ) : (
          <Controller
            name={`${name}.correctIndex`}
            control={control}
            render={({ field }) => (
              <>
                <Typography className="mss__label" id={`${cardId}__label_answers`}>
                  Answers
                </Typography>

                <Box
                  className="answerList"
                  role="radiogroup"
                  aria-labelledby={`${cardId}__label_answers`}
                >
                  {question.answers.map((_answer, answerIndex) => (
                    <AnswerRow
                      key={answerIndex}
                      id={cardId}
                      control={control}
                      name={name}
                      questionNumber={number}
                      index={answerIndex}
                      optionKey={QUIZ_OPTION_KEYS[answerIndex]}
                      isCorrect={field.value === answerIndex}
                      onMarkCorrect={() => field.onChange(answerIndex)}
                    />
                  ))}
                </Box>
              </>
            )}
          />
        )}
      </Box>
    </Box>
  );
};
